import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { Gantt, ViewMode } from "gantt-task-react";
import "gantt-task-react/dist/index.css";
import TaskListHeaderFr from "@/features/task/components/gantt/TaskListHeaderFr";
import TaskListTableFr from "@/features/task/components/gantt/TaskListTableFr";
import TaskTimeLineFilters from "@/features/task/components/TaskTimeLineFilters";
import useTimelineTasks from "@/features/task/hooks/useTimelineTasks";
import useTicketSuffix from "@/features/task/hooks/useTicketSuffix";
import { useAuth } from "@/auth/useAuth";
import { buildDefaultTaskFilters, getRestrictedAssigneeId } from "@/features/task/utils/filterUtils";
const TaskTimelinePage = () => {
    const ganttContainerRef = useRef(null);
    const { user } = useAuth();
    const restrictedAssigneeId = useMemo(() => getRestrictedAssigneeId(user), [user]);
    const isRestrictedUser = Boolean(restrictedAssigneeId);
    const [filters, setFilters] = useState(() => buildDefaultTaskFilters(restrictedAssigneeId));
    const { tasks, loading, error } = useTimelineTasks(filters);
    const { formatTicketCode } = useTicketSuffix();
    const [viewMode, setViewMode] = useState(ViewMode.Week);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        return `${year}-${month}`;
    });
    useEffect(() => {
        if (!restrictedAssigneeId) {
            return;
        }
        setFilters(prev => {
            const currentAssignees = Array.isArray(prev.assignees) ? prev.assignees : [];
            if (currentAssignees.length === 1 && currentAssignees[0] === restrictedAssigneeId) {
                return prev;
            }
            return {
                ...prev,
                assignees: [restrictedAssigneeId],
            };
        });
    }, [restrictedAssigneeId]);
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
            return;
        }
        if (typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("jsdom")) {
            return;
        }
        const reloadFlagKey = "taskTimelinePageReloaded";
        try {
            const hasReloaded = window.sessionStorage.getItem(reloadFlagKey);
            if (!hasReloaded) {
                window.sessionStorage.setItem(reloadFlagKey, "true");
                window.setTimeout(() => {
                    window.location.reload();
                }, 0);
            }
        }
        catch {
            // If sessionStorage is not accessible, skip the forced reload to avoid breaking the page.
        }
    }, []);
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => {
            if (filterName === "assignees") {
                if (isRestrictedUser && restrictedAssigneeId) {
                    return {
                        ...prev,
                        assignees: [restrictedAssigneeId],
                    };
                }
                return {
                    ...prev,
                    assignees: Array.isArray(value) ? value : [],
                };
            }
            const nextFilters = {
                ...prev,
                [filterName]: value,
            };
            if (isRestrictedUser && restrictedAssigneeId) {
                nextFilters.assignees = [restrictedAssigneeId];
            }
            return nextFilters;
        });
    };
    const handleResetFilters = () => {
        setFilters(buildDefaultTaskFilters(restrictedAssigneeId));
    };
    const getMonthBounds = (value) => {
        const [yearString, monthString] = value.split("-");
        const year = Number(yearString);
        const monthIndex = Number(monthString) - 1;
        const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
        const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
        return { start, end };
    };
    const filteredTasks = useMemo(() => {
        if (viewMode !== ViewMode.Day || !selectedMonth) {
            return tasks;
        }
        const { start: monthStart, end: monthEnd } = getMonthBounds(selectedMonth);
        return tasks.filter(task => {
            const taskStart = task.created_at ? new Date(task.created_at) : null;
            const taskEnd = task.due_date ? new Date(task.due_date) : taskStart;
            if (!taskStart && !taskEnd) {
                return true;
            }
            const startDate = taskStart ?? taskEnd ?? monthStart;
            const endDate = taskEnd ?? taskStart ?? monthEnd;
            return startDate <= monthEnd && endDate >= monthStart;
        });
    }, [tasks, viewMode, selectedMonth]);
    const ganttTasks = filteredTasks.map(t => {
        const shortTitle = t.title.length > 20 ? t.title.substring(0, 20) + "..." : t.title;
        return {
            id: t.id.toString(),
            name: `${formatTicketCode(t.id)} ${shortTitle}`.trim(),
            start: t.created_at ? new Date(t.created_at) : new Date(),
            end: t.due_date ? new Date(t.due_date) : new Date(),
            type: "task",
            progress: 0,
        };
    });
    const ganttViewDate = useMemo(() => {
        if (viewMode !== ViewMode.Day || !selectedMonth) {
            return undefined;
        }
        const { start } = getMonthBounds(selectedMonth);
        return start;
    }, [selectedMonth, viewMode]);
    useEffect(() => {
        if (viewMode !== ViewMode.Week) {
            return;
        }
        if (typeof window === "undefined") {
            return;
        }
        const container = ganttContainerRef.current;
        if (!container) {
            return;
        }
        const replaceWeekPrefix = () => {
            const weekLabels = container.querySelectorAll("text");
            weekLabels.forEach(node => {
                const label = node.textContent ?? "";
                if (/^W\d+/.test(label)) {
                    node.textContent = label.replace(/^W/, "S");
                }
            });
        };
        const animationFrame = window.requestAnimationFrame(replaceWeekPrefix);
        return () => {
            window.cancelAnimationFrame(animationFrame);
        };
    }, [viewMode, ganttTasks, ganttViewDate]);
    return (_jsx("div", { className: "p-4 bg-gray-50 min-h-screen", children: _jsxs("div", { className: "grid grid-cols-[250px_1fr] gap-6", children: [_jsx("aside", { children: _jsx(TaskTimeLineFilters, { filters: filters, onFilterChange: handleFilterChange, onResetFilters: handleResetFilters }) }), _jsxs("section", { children: [loading && _jsx("p", { children: "Chargement..." }), error && _jsxs("p", { className: "text-red-500", children: ["Erreur : ", error] }), !loading && !error && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mb-4 flex flex-col gap-2 sm:flex-row sm:items-center", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("label", { htmlFor: "view-mode", children: "Vue" }), _jsxs("select", { id: "view-mode", value: viewMode, onChange: e => {
                                                        const newViewMode = e.target.value;
                                                        setViewMode(newViewMode);
                                                    }, className: "border p-2 rounded", children: [_jsx("option", { value: ViewMode.Day, children: "Jour" }), _jsx("option", { value: ViewMode.Week, children: "Semaine" }), _jsx("option", { value: ViewMode.Month, children: "Mois" }), _jsx("option", { value: ViewMode.Year, children: "Ann\u00E9e" })] })] }), viewMode === ViewMode.Day && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("label", { htmlFor: "timeline-month", children: "Mois" }), _jsx("input", { id: "timeline-month", type: "month", value: selectedMonth, onChange: event => setSelectedMonth(event.target.value), className: "border p-2 rounded" })] }))] }), _jsx("div", { ref: ganttContainerRef, children: ganttTasks.length > 0 ? (_jsx(Gantt, { tasks: ganttTasks, viewMode: viewMode, viewDate: ganttViewDate, locale: "fr-FR", TaskListHeader: TaskListHeaderFr, TaskListTable: TaskListTableFr, onDateChange: () => { }, onProgressChange: () => { }, onDelete: () => { } })) : (_jsxs("div", { children: [_jsx(TaskListHeaderFr, { headerHeight: 50, rowWidth: "155px", fontFamily: "Arial", fontSize: "14px" }), _jsx(TaskListTableFr, { tasks: [], rowHeight: 50, rowWidth: "155px", fontFamily: "Arial", fontSize: "14px", locale: "fr-FR", selectedTaskId: "", setSelectedTask: () => { }, onExpanderClick: () => { } })] })) })] }))] })] }) }));
};
export default TaskTimelinePage;
